import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLanguage } from '../../contexts/LanguageContext';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { ExternalLink, FileText, Download } from 'lucide-react';
import { captureEvent } from '../../lib/analytics/posthog';
import { cn, sanitizeDownloadFilename } from '../../lib/utils';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentPdfViewerProps {
  pdfBlob: Blob | null;
  pdfUrl: string | null;
  isLoading: boolean;
  hasDocumentInfo: boolean;
  fileName?: string;
  pageError?: string | null;
  onLoadDocument: () => void;
  /** Extra padding on the PDF scroll area (e.g. floating action bar on mobile) */
  scrollAreaClassName?: string;
}

const DocumentPdfViewer: React.FC<DocumentPdfViewerProps> = ({
  pdfBlob,
  pdfUrl,
  isLoading,
  hasDocumentInfo,
  fileName,
  pageError,
  onLoadDocument,
  scrollAreaClassName,
}) => {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(Math.floor(entry.contentRect.width));
      }
    });
    observer.observe(node);
    setContainerWidth(Math.floor(node.getBoundingClientRect().width));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setNumPages(null);
    setRenderError(null);
  }, [pdfBlob]);

  const handleOpenInBrowser = useCallback(() => {
    if (pdfUrl) {
      captureEvent('document_pdf_open_in_browser_clicked');
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  }, [pdfUrl]);

  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    captureEvent('document_pdf_download_clicked');
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = sanitizeDownloadFilename(fileName);
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [fileName, pdfUrl]);

  const viewerToolbar = pdfBlob && !renderError && (
    <div className="mb-2 flex shrink-0 flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
        <Download className="h-4 w-4" />
        {t('downloadDocument')}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={handleOpenInBrowser}>
        <ExternalLink className="h-4 w-4" />
        {t('openPdfInBrowser')}
      </Button>
    </div>
  );

  const onDocumentLoadSuccess = useCallback(({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
    setRenderError(null);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setRenderError(t('pdfRenderError'));
  }, [t]);

  const pageWidth = Math.max(containerWidth - 16, 280);

  return (
    <div ref={containerRef} className="flex h-full min-h-0 flex-col">
      {pageError && (
        <Alert variant="destructive" className="mb-2 shrink-0">
          {pageError}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : pdfBlob ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {viewerToolbar}
          <div className={cn('min-h-0 flex-1 overflow-y-auto overflow-x-hidden', scrollAreaClassName)}>
          {renderError ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <Alert variant="destructive">{renderError}</Alert>
              {pdfUrl && (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="outline" onClick={handleDownload}>
                    <Download className="h-4 w-4" />
                    {t('downloadDocument')}
                  </Button>
                  <Button variant="outline" onClick={handleOpenInBrowser}>
                    <ExternalLink className="h-4 w-4" />
                    {t('openPdfInBrowser')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Document
              file={pdfBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              }
              className="flex flex-col items-center gap-4 py-2"
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                    className="shadow-sm"
                  />
                ))}
            </Document>
          )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <p className="text-muted-foreground">{t('noDocumentLoaded')}</p>
          {hasDocumentInfo && (
            <Button onClick={onLoadDocument} disabled={isLoading}>
              <FileText className="h-4 w-4" />
              {t('displayFile')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentPdfViewer;
